import React from 'react';
import Badge from "@amzn/awsui-components-react/polaris/badge";
import PANNVerifyJiraTable from "../Jira/PANNVerifyJiraTable";
import { isPANNApplicable, isPANNVerified } from '../Common/ParseUtils';
import { toTitleCase } from '../Common/FormatUtils';
import {
    Box,
    ColumnLayout,
    Container,
    Header,
    SpaceBetween,
    Spinner
} from '@amzn/awsui-components-react/polaris';
import '../../styles/center.scss';

const ValueWithLabel = ({ label, children }) => (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <div>{children}</div>
    </div>
);

function PANNRunBadge(props) {
    if(props.numPannAnalyzed > 0) {
        return <Badge color="green">Yes</Badge>;
    }
    return <Badge color="red">No</Badge>;
}

function ApplicableIssuesLabel(props) {
    if(props.numPannAnalyzed > 0) {
        return <ValueWithLabel label="Applicable Issues">{props.numPannApplicable} / {props.totalIssues}</ValueWithLabel>
    }
    return <ValueWithLabel label="Total Issues">{props.totalIssues}</ValueWithLabel>
}

function BackfillVerifyPanel(props) {
    if(!props.issues || props.issues.length < 1) {
        return '';
    }

    return (
        <SpaceBetween size="s">
            {props.issues.map((item, index) => (
                <Container
                    key={index}
                    header={
                        <Header 
                            variant="h4"
                        >
                            {item.month}
                        </Header>
                    }
                >
                    <PANNVerifyJiraTable index={index} jiras={item.issues} pann_run={props.numPannAnalyzed > 0 ? true : false} />
                </Container>
            ))}
        </SpaceBetween>
    );
}

export default function PANNVerifyPanel(props) {
    const [loading, setLoading] = React.useState(false); 
    const [patch05Issues, setPatch05Issues] = React.useState([]);
    const [patch01Issues, setPatch01Issues] = React.useState([]);
    const [patch01IssuesDelorean, setPatch01IssuesDelorean] = React.useState([]);
    const [backfillIssues, setBackfillIssues] = React.useState([]);
    const [isBackfill, setIsBackfill] = React.useState(false); 
    const [backfillStartBulletin, setBackfillStartBulletin] = React.useState('--'); 
    const [backfillDevice, setBackfillDevice] = React.useState('--'); 
    const [numPannAnalyzed, setNumPannAnalyzed] = React.useState(0); 
    const [numPannApplicable, setNumPannApplicable] = React.useState(0); 
    const [totalIssues, setTotalIssues] = React.useState(0); 

    React.useEffect(() => {
        parsePANNResults(props.results);
    }, [props.filename]);

    const parsePANNResults = async(results) => {
        setLoading(true);
        let numPannAnalyzed = 0;
        let numPannApplicable = 0;
        let totalIssues = 0;

        if('backfill' in results) { //Parse as PANN backfill file (which has a different format)
            setIsBackfill(true);
            try{
                let issues = [];
                
                try { //Set first backfill month
                    setBackfillStartBulletin(results['backfill'][0]['asb_month']);
                }
                catch(e){}

                try { //Parse backfilled device name from job args
                    let device_name = results['backfill'][0]['args'];
                    device_name = device_name.substring(device_name.indexOf("--device") + 9).trim();
                    device_name = device_name.substring(0, device_name.indexOf(' '));
                    setBackfillDevice(toTitleCase(device_name));
                }
                catch(e){}

                for(const backfill_month of results['backfill']) {
                    let backfill_month_issues = [];

                    for(const issue of backfill_month['issues']['patch05']) { //Parse Patch05
                        let pann_verified = isPANNVerified(issue);
                        let applicable = isPANNApplicable(issue);
                        numPannAnalyzed += pann_verified ? 1 : 0;
                        numPannApplicable += applicable ? 1 : 0;
                        totalIssues++;

                        backfill_month_issues.push({
                            'project': issue['project']['key'],
                            'summary': issue['summary'],
                            'type': 'patch05',
                            'pann_verified': pann_verified,
                            'applicable': applicable
                        });
                    }
                    
                    issues.push({
                        'month': backfill_month['asb_month'],
                        'issues': backfill_month_issues
                    });
                }
                setBackfillIssues(issues);
            }
            catch(e){}
        }
        else { //Parse as normal PANN results file
            try {
                let issues = [];
                for(const issue of results['issues']['patch05']) { //Parse Patch05
                    let pann_verified = isPANNVerified(issue);
                    let applicable = isPANNApplicable(issue);
                    numPannAnalyzed += pann_verified ? 1 : 0;
                    numPannApplicable += applicable ? 1 : 0;
                    totalIssues++;

                    issues.push({
                        'project': issue['project']['key'],
                        'summary': issue['summary'],
                        'type': 'patch05',
                        'pann_verified': pann_verified,
                        'applicable': applicable
                    });
                }
                setPatch05Issues(issues);
            }
            catch(e){}

            try {
                let issues = [];
                for(const issue of results['issues']['patch01_delorean']) { //Parse Patch01 Delorean
                    let pann_verified = isPANNVerified(issue);
                    let applicable = isPANNApplicable(issue);
                    numPannAnalyzed += pann_verified ? 1 : 0;
                    numPannApplicable += applicable ? 1 : 0;
                    totalIssues++;

                    issues.push({
                        'project': issue['project']['key'],
                        'summary': issue['summary'],
                        'type': 'patch01_delorean',
                        'pann_verified': pann_verified,
                        'applicable': applicable
                    });
                }
                setPatch01IssuesDelorean(issues);
            }
            catch(e){}

            try {
                let issues = [];
                for(const issue of results['issues']['patch01_standard']) { //Parse Patch01 Standard
                    let pann_verified = isPANNVerified(issue);
                    let applicable = isPANNApplicable(issue);
                    numPannAnalyzed += pann_verified ? 1 : 0;
                    numPannApplicable += applicable ? 1 : 0;
                    totalIssues++;

                    issues.push({
                        'project': issue['project']['key'],
                        'summary': issue['summary'],
                        'type': 'patch01_standard',
                        'pann_verified': pann_verified,
                        'applicable': applicable
                    });
                }
                setPatch01Issues(issues);
            }
            catch(e){}
        }

        setNumPannAnalyzed(numPannAnalyzed);
        setNumPannApplicable(numPannApplicable);
        setTotalIssues(totalIssues);
        setLoading(false);
    }

    if(loading) {
        return (
            <Container header={<Header variant="h3">PANN Results</Header>}>
                <div align="center">
                    <Spinner size="large" />
                </div>
            </Container>
        );
    }

    if(isBackfill) { //Return a slightly different view for backfill jobs, centered around each month
        return(
            <SpaceBetween size="m">
                <Container 
                    header={
                        <Header 
                            variant="h3"
                        >
                            PANN Backfill Results
                        </Header>
                    }
                >
                    <ColumnLayout columns={4} variant="text-grid">
                        <SpaceBetween size="l">
                            <ValueWithLabel label="PANN Analysis Complete"><PANNRunBadge numPannAnalyzed={numPannAnalyzed} /></ValueWithLabel>
                            <ValueWithLabel label="Backfill Device"><Badge color="blue">{backfillDevice}</Badge></ValueWithLabel>
                            <ValueWithLabel label="Backfill Start Bulletin">{backfillStartBulletin}</ValueWithLabel>
                            <ApplicableIssuesLabel numPannAnalyzed={numPannAnalyzed}  numPannApplicable={numPannApplicable} totalIssues={totalIssues} />
                        </SpaceBetween>
                    </ColumnLayout>
                </Container>
                <BackfillVerifyPanel issues={backfillIssues} numPannAnalyzed={numPannAnalyzed} />
            </SpaceBetween>
        )
    }

    return(
        <SpaceBetween size="m">
            <Container 
                header={
                    <Header 
                        variant="h3"
                    >
                        PANN Results
                    </Header>
                }
            >
                <ColumnLayout columns={4} variant="text-grid">
                    <SpaceBetween size="l">
                        <ValueWithLabel label="PANN Analysis Complete"><PANNRunBadge numPannAnalyzed={numPannAnalyzed} /></ValueWithLabel>
                        <ApplicableIssuesLabel numPannAnalyzed={numPannAnalyzed}  numPannApplicable={numPannApplicable} totalIssues={totalIssues} />
                        <ValueWithLabel label="Patch05 Issues">{patch05Issues.length}</ValueWithLabel>
                        <ValueWithLabel label="Patch01 (Delorean)">{patch01IssuesDelorean.length}</ValueWithLabel>
                        <ValueWithLabel label="Patch01 (Standard)">{patch01Issues.length}</ValueWithLabel>
                    </SpaceBetween>
                </ColumnLayout>
            </Container>
            <Container 
                header={
                    <Header 
                        variant="h4"
                    >
                        Patch05 Jiras - {patch05Issues.length}
                    </Header>
                }
            >
                <PANNVerifyJiraTable jiras={patch05Issues} pann_run={numPannAnalyzed > 0 ? true : false} />
            </Container>
            <Container 
                header={
                    <Header 
                        variant="h4"
                    >
                        Patch01 Jiras (Delorean) - {patch01IssuesDelorean.length}
                    </Header>
                }
            >
                <PANNVerifyJiraTable jiras={patch01IssuesDelorean} pann_run={numPannAnalyzed > 0 ? true : false}/>
            </Container>
            <Container 
                header={
                    <Header 
                        variant="h4"
                    >
                        Patch01 Jiras (Standard) - {patch01Issues.length}
                    </Header>
                }
            >
                <PANNVerifyJiraTable jiras={patch01Issues} pann_run={numPannAnalyzed > 0 ? true : false}/>
            </Container>
        </SpaceBetween>
    )
}